import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Select } from '@aline/common';
import { useTheme } from '@mui/material';
import { usePrevious } from 'react-use';
import { FormSubmitButtons } from 'src/components';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import type { AlineMarkdownEditorRef } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/types';
import { getCommentMarkdownMentions } from 'src/components/comments/comment-markdown-mentions';
import { PrivateCommentMentionWarning } from 'src/components/comments/PrivateCommentMentionWarning';
import { GrantMentionedUsersAccessDialog } from 'src/components/simple-editor/GrantMentionedUsersAccessDialog';
import { useMentionedAccountsWithoutAccessForUserIds } from 'src/components/simple-editor/hooks';
import type { MentionEntity } from 'src/components/simple-editor/types';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useMaybeWorkflowParams } from 'src/helpers';
import { useCreateCommentReply } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  buttonContainer: {
    marginTop: theme.spacing(1),
  },
}));

interface ReplyFormProps {
  rootCommentId: string;
  disabled?: boolean;
  isPrivateThread?: boolean;
}

export const replyEditorId = 'reply-comment-editor';

export function AddReplyForm({
  disabled,
  isPrivateThread = false,
  rootCommentId,
}: ReplyFormProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const {
    comments: { setActivePanel },
  } = useEditorTabStateActions();
  const currentUserId = useCurrentUserId();
  const [markdown, setMarkdown] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [mentionedEmails, setMentionedEmails] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const editorRef = useRef<AlineMarkdownEditorRef>(null);
  const { documentId, workflowId } = useMaybeWorkflowParams();
  const { createCommentReply, loading: creatingReply } = useCreateCommentReply();
  const [pendingMarkdown, setPendingMarkdown] = useState<string | null>(null);
  const [emailsWithoutAccess, setEmailsWithoutAccess] = useState<string[]>([]);

  const activePanel = useEditorTabStateSelector(state => state.comments.activePanel);
  const prevActivePanel = usePrevious(activePanel);
  const resetEditor = useCallback(() => {
    editorRef.current?.clear();
    setMarkdown('');
    setMentionedUserIds([]);
    setMentionedEmails([]);
    setPendingMarkdown(null);
    setEmailsWithoutAccess([]);
  }, []);

  // Reset and focus editor when panel becomes active
  useEffect(() => {
    if (activePanel === 'ViewThread' && prevActivePanel !== 'ViewThread') {
      resetEditor();
    }
  }, [activePanel, prevActivePanel, resetEditor]);

  const mentionEntity: MentionEntity = workflowId
    ? { __typename: 'Workflow', id: workflowId }
    : { __typename: 'Document', id: documentId };

  const {
    canEditMentionEntity,
    getEmailsWithoutAccessForUserIds,
    getMentionedExternalUsersForUserIds,
    mentionEntityOwner,
  } = useMentionedAccountsWithoutAccessForUserIds({
    isPrivate: isPrivateThread,
    mentionEntity,
  });
  const mentionedExternalUsers = getMentionedExternalUsersForUserIds(
    mentionedUserIds,
    mentionedEmails
  );

  function syncMentionState(nextMarkdown: string): void {
    setMarkdown(nextMarkdown);

    const { directMentionEmails, mentionedUserIds: userMentionIds } = getCommentMarkdownMentions(
      editorRef.current?.getEditor(),
      nextMarkdown
    );
    setMentionedUserIds(userMentionIds);
    setMentionedEmails(directMentionEmails);
  }

  async function handleSubmit(): Promise<void> {
    const trimmed = (editorRef.current?.getMarkdown() ?? markdown).trim();
    if (!trimmed) return;

    const { directMentionEmails, mentionedUserIds: userMentionIds } = getCommentMarkdownMentions(
      editorRef.current?.getEditor(),
      trimmed
    );
    const nextEmailsWithoutAccess = getEmailsWithoutAccessForUserIds(
      userMentionIds,
      directMentionEmails
    );
    if (nextEmailsWithoutAccess.length) {
      setEmailsWithoutAccess(nextEmailsWithoutAccess);
      setPendingMarkdown(trimmed);
      setOpen(true);
      return;
    }

    await handleCreateReply(trimmed);
  }

  async function handleCreateReply(text: string): Promise<void> {
    if (!currentUserId) return;

    const { data } = await createCommentReply(rootCommentId, {
      variables: {
        input: {
          commentProps: {
            authorId: currentUserId,
            commentableId: documentId,
            commentableType: Apollo.CommentableType.Document,
            nodeRefId: null,
            parentCommentId: rootCommentId,
            text,
          },
        },
      },
    });
    if (!data) return;

    resetEditor();
  }

  function handleReset(): void {
    resetEditor();

    setActivePanel('ViewComments');
  }

  return (
    <>
      {isPrivateThread && <PrivateCommentMentionWarning users={mentionedExternalUsers} />}
      <AlineMarkdownEditor
        key={replyEditorId}
        ref={editorRef}
        autoFocus
        dataCy={Select.document.editor.rightDrawer.comments.replyInput}
        maxHeight={160}
        mentions={{
          allowEmailMentions: true,
          documentId,
          enabledTypes: [Apollo.MentionEntitySearchType.User],
          workflowId,
        }}
        minHeight={80}
        placeholder="Add your reply text..."
        readOnly={disabled}
        style={{ backgroundColor: theme.palette.common.white, borderRadius: 3 }}
        onChange={syncMentionState}
        onSubmit={handleSubmit}
      />
      <FormSubmitButtons
        className={classes.buttonContainer}
        disableCancel={creatingReply}
        disableSubmit={disabled}
        loading={creatingReply}
        primaryButtonText="Reply"
        onClickCancel={handleReset}
        onClickSubmit={handleSubmit}
      />
      <GrantMentionedUsersAccessDialog
        emailsWithoutAccess={emailsWithoutAccess}
        mentionEntity={mentionEntity}
        mentionEntityOwner={mentionEntityOwner}
        open={open}
        setOpen={setOpen}
        variant={canEditMentionEntity ? 'grant-access' : 'request-access'}
        onSubmit={() => {
          if (!pendingMarkdown) return;

          void handleCreateReply(pendingMarkdown);
        }}
      />
    </>
  );
}
